import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { adminAPI } from '../../api/client'
import { listLevelOptions, biasedDifficultyRange, type LevelOption } from '@enghustle/shared'
import {
  AICostControlTab,
  AIRequestLogsTab,
  AISystemCostMonitorTab,
  BatchGenJobStatusTab,
  ContentReviewApprovalTab,
  BulkCSVImportTab,
  CouponManagerTab,
} from './AdminTabsExtra'
import ListeningContentTab from './ListeningContentTab'
import ExamFrameworksTab from './ExamFrameworksTab'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

type AdminTab = 'content' | 'questions' | 'stats' | 'ai' | 'ai_cost' | 'ai_logs' | 'ai_cost_monitor' | 'users' | 'monitor' | 'config' | 'generator' | 'batch_status' | 'content_review' | 'bulk_import' | 'revenue' | 'coupon' | 'notifications' | 'analytics' | 'listening_content' | 'exams'

const TAB_META: Record<AdminTab, { label: string; icon: string }> = {
  content:          { label: 'Nội dung',       icon: 'article' },
  questions:        { label: 'Câu hỏi',        icon: 'quiz' },
  stats:            { label: 'Thống kê',       icon: 'bar_chart' },
  ai:               { label: 'AI Config',      icon: 'psychology' },
  ai_cost:          { label: 'AI Cost Control', icon: 'price_check' },
  ai_logs:          { label: 'AI Request Logs', icon: 'receipt_long' },
  ai_cost_monitor:  { label: 'AI Cost Monitor', icon: 'monitor_heart' },
  generator:        { label: 'AI Generator',   icon: 'auto_awesome' },
  batch_status:     { label: 'Batch Jobs',     icon: 'batch_prediction' },
  content_review:   { label: 'Duyệt nội dung', icon: 'fact_check' },
  bulk_import:      { label: 'Bulk Import',    icon: 'upload_file' },
  users:            { label: 'Người dùng',     icon: 'group' },
  revenue:          { label: 'Doanh thu',      icon: 'payments' },
  coupon:           { label: 'Coupon',         icon: 'local_offer' },
  notifications:    { label: 'Thông báo',      icon: 'notifications' },
  monitor:          { label: 'Giám sát',       icon: 'monitoring' },
  config:           { label: 'Cấu hình',       icon: 'settings' },
  analytics:        { label: 'Analytics',      icon: 'analytics' },
  listening_content: { label: 'Listening Content', icon: 'headphones' },
  exams:            { label: 'Kỳ thi (Exams)', icon: 'workspace_premium' },
}

export default function AdminPage() {
  const { tab: tabParam } = useParams<{ tab?: string }>()
  const navigate = useNavigate()
  const tab = (tabParam as AdminTab) || 'content'

  const tabs = Object.entries(TAB_META) as [AdminTab, { label: string; icon: string }][]

  function setTab(id: AdminTab) {
    navigate(`/admin/${id}`, { replace: true })
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f6f6ff', fontFamily: "'Manrope', sans-serif", color: '#272e42' }}>
      {/* Sidebar */}
      <aside style={{ width: '15rem', flexShrink: 0, background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(24px)', display: 'flex', flexDirection: 'column', padding: '1.5rem 1rem', borderRight: '1px solid #eef0ff', position: 'sticky', top: 0, height: '100vh', overflowY: 'auto' }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '2rem', padding: '0 0.5rem' }}>
          <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.75rem', background: 'linear-gradient(135deg,#004be2,#809bff)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,75,226,0.3)' }}>
            <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>admin_panel_settings</span>
          </div>
          <div>
            <H style={{ display: 'block', fontWeight: 900, fontSize: '1rem', color: '#004be2', lineHeight: 1 }}>Admin</H>
            <span style={{ fontSize: '0.5625rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#a5adc6' }}>EngHustle</span>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {tabs.map(([id, meta]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.625rem', padding: '0.75rem 0.875rem', borderRadius: '0.875rem', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', transition: 'all 0.2s',
                background: tab === id ? '#004be2' : 'transparent',
                color: tab === id ? 'white' : '#535b71',
                boxShadow: tab === id ? '0 4px 12px rgba(0,75,226,0.25)' : 'none',
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '1.125rem', fontVariationSettings: tab === id ? "'FILL' 1" : "'FILL' 0" }}>{meta.icon}</span>
              <span style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{meta.label}</span>
            </button>
          ))}
        </nav>

        <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#eef0ff', borderRadius: '1rem', fontSize: '0.75rem', color: '#535b71' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '0.875rem', color: '#b41340', verticalAlign: 'middle' }}>lock</span>
          {' '}Chỉ Admin
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, padding: '2rem', minWidth: 0 }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <span className="material-symbols-outlined" style={{ color: '#b41340', fontSize: '1rem', fontVariationSettings: "'FILL' 1" }}>{TAB_META[tab].icon}</span>
            <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#b41340' }}>Admin</span>
          </div>
          <H style={{ display: 'block', fontSize: '1.75rem', fontWeight: 900, letterSpacing: '-0.03em' }}>{TAB_META[tab].label}</H>
        </div>

        <div className="admin-page">
          {tab === 'content' && <ContentTab />}
          {tab === 'questions' && <QuestionsTab />}
          {tab === 'stats' && <StatsTab />}
          {tab === 'ai' && <AIConfigTab />}
          {tab === 'ai_cost' && <AICostControlTab />}
          {tab === 'ai_logs' && <AIRequestLogsTab />}
          {tab === 'ai_cost_monitor' && <AISystemCostMonitorTab />}
          {tab === 'generator' && <ContentGeneratorTab />}
          {tab === 'batch_status' && <BatchGenJobStatusTab />}
          {tab === 'content_review' && <ContentReviewApprovalTab />}
          {tab === 'bulk_import' && <BulkCSVImportTab />}
          {tab === 'users' && <UsersTab />}
          {tab === 'revenue' && <RevenueTab />}
          {tab === 'coupon' && <CouponManagerTab />}
          {tab === 'notifications' && <NotificationsTab />}
          {tab === 'monitor' && <MonitorTab />}
          {tab === 'config' && <SystemConfigTab />}
          {tab === 'analytics' && <AnalyticsTab />}
          {tab === 'listening_content' && <ListeningContentTab />}
          {tab === 'exams' && <ExamFrameworksTab />}
        </div>
      </main>
    </div>
  )
}

// ─── JSON Import Panel ────────────────────────────────────────────────────────

const JSON_EXAMPLES: Record<string, { label: string; example: object[] }> = {
  content_items: {
    label: 'Content Items',
    example: [
      {
        // title: string — tiêu đề bài nghe/đọc
        title: 'Daily Weather Talk',
        // source: 'ted' | 'bbc' | 'guardian' | 'vstep_internal' | 'ai_generated'
        source: 'vstep_internal',
        // url: string | '' — để trống nếu chưa có audio (listening → pending_audio)
        url: '',
        // skill: 'listening' | 'reading' | 'writing' | 'speaking'
        skill: 'listening',
        // cefr_level: 'A2' | 'B1' | 'B2' | 'C1'
        cefr_level: 'B1',
        // vstep_parts: string — phân cách bằng dấu phẩy, e.g. "part1,part2"
        vstep_parts: 'part1,part2',
        // topic_tags: string — phân cách bằng dấu phẩy
        topic_tags: 'weather,daily life',
        // difficulty_score: number 1–10
        difficulty_score: 5,
        // duration_seconds: number | '' — thời lượng audio (giây)
        duration_seconds: '',
        // word_count: number | ''
        word_count: '',
        // transcript: string — nội dung transcript
        transcript: 'Man: Have you seen the forecast? Woman: Yes, rain tomorrow.',
      },
    ],
  },
  questions: {
    label: 'Questions',
    example: [
      {
        // question_text: string — nội dung câu hỏi (bắt buộc)
        question_text: 'What is the main topic of the conversation?',
        // skill: 'listening' | 'reading' | 'writing' | 'speaking' (bắt buộc)
        skill: 'listening',
        // vstep_part: 'part1' | 'part2' | 'part3' | '' (tùy chọn)
        vstep_part: 'part1',
        // cefr_level: 'A2' | 'B1' | 'B2' | 'C1' (bắt buộc)
        cefr_level: 'B1',
        // option_a/b/c/d: string — các đáp án MCQ
        option_a: 'Weather',
        option_b: 'Traffic',
        option_c: 'Sports',
        option_d: 'Food',
        // correct_option: 'A' | 'B' | 'C' | 'D' (bắt buộc)
        correct_option: 'A',
        // explanation: string — giải thích đáp án (tùy chọn)
        explanation: 'The speakers discuss the weather forecast.',
        // content_title: string — bắt buộc nếu skill=listening và có transcript
        content_title: 'Daily Weather Talk',
        // transcript: string — tự động tạo content_item nếu skill=listening
        transcript: 'Man: Have you seen the forecast? Woman: Yes, rain tomorrow.',
      },
    ],
  },
  vocabulary: {
    label: 'Vocabulary',
    example: [
      {
        // word: string — từ vựng (bắt buộc)
        word: 'inference',
        // definition_vi: string — nghĩa tiếng Việt (bắt buộc)
        definition_vi: 'suy luận',
        // definition_en: string — nghĩa tiếng Anh (tùy chọn)
        definition_en: 'the act of reaching a conclusion based on evidence',
        // example_sentence: string — câu ví dụ (tùy chọn)
        example_sentence: 'We can make an inference from the data.',
        // topic_tag: string — chủ đề (tùy chọn)
        topic_tag: 'academic',
        // cefr_level: 'A2' | 'B1' | 'B2' | 'C1' (bắt buộc)
        cefr_level: 'B2',
      },
    ],
  },
}

function JsonImportPanel({ defaultType, onSuccess }: { defaultType: string; onSuccess: () => void }) {
  const [type, setType] = useState(defaultType)
  const [jsonText, setJsonText] = useState(() => JSON.stringify(JSON_EXAMPLES[defaultType]?.example ?? [], null, 2))
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null)
  const [loading, setLoading] = useState(false)

  function loadExample(t: string) {
    setType(t)
    setJsonText(JSON.stringify(JSON_EXAMPLES[t]?.example ?? [], null, 2))
    setStatus(null)
  }

  async function handleImport() {
    setStatus(null)
    let parsed: unknown
    try {
      parsed = JSON.parse(jsonText)
    } catch {
      setStatus({ ok: false, msg: 'JSON không hợp lệ. Kiểm tra lại cú pháp.' })
      return
    }
    if (!Array.isArray(parsed)) {
      setStatus({ ok: false, msg: 'JSON phải là một mảng [...] các object.' })
      return
    }
    setLoading(true)
    try {
      const res = await fetch('/api/v1/admin/import/json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ type, rows: parsed }),
      })
      const data = await res.json()
      if (!res.ok || !data.success) {
        const errMsg = data.errors
          ? data.errors.slice(0, 5).map((e: any) => `Row ${e.row} [${e.field}]: ${e.message}`).join('\n')
          : (data.error ?? 'Import thất bại')
        setStatus({ ok: false, msg: errMsg })
      } else {
        setStatus({ ok: true, msg: `✅ Đã import ${data.data.inserted}/${data.data.total} items thành công.` })
        onSuccess()
      }
    } catch {
      setStatus({ ok: false, msg: 'Lỗi kết nối. Thử lại sau.' })
    } finally {
      setLoading(false)
    }
  }

  const panelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }
  const rowStyle: React.CSSProperties = { display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }
  const textareaStyle: React.CSSProperties = {
    width: '100%', minHeight: '260px', fontFamily: 'monospace', fontSize: '0.8rem',
    padding: '0.75rem', border: '1px solid #dee2e6', borderRadius: '0.5rem',
    background: '#f8f9fa', resize: 'vertical', boxSizing: 'border-box',
  }
  const noteStyle: React.CSSProperties = {
    fontSize: '0.78rem', color: '#6c757d', background: '#f0f4ff',
    border: '1px solid #c7d4f5', borderRadius: '0.5rem', padding: '0.5rem 0.75rem',
  }

  return (
    <div style={panelStyle}>
      <div style={rowStyle}>
        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Loại dữ liệu:</span>
        {Object.entries(JSON_EXAMPLES).map(([k, v]) => (
          <button
            key={k}
            onClick={() => loadExample(k)}
            style={{
              padding: '0.25rem 0.75rem', borderRadius: '1rem', border: '1px solid',
              cursor: 'pointer', fontSize: '0.8rem', fontWeight: type === k ? 700 : 400,
              background: type === k ? '#004be2' : 'white',
              color: type === k ? 'white' : '#004be2',
              borderColor: '#004be2',
            }}
          >{v.label}</button>
        ))}
      </div>

      <div style={noteStyle}>
        💡 Nhập JSON là mảng các object. Các trường có comment <code>// bắt buộc</code> là required.
        Nhấn vào loại dữ liệu ở trên để xem ví dụ có chú thích.
      </div>

      <textarea
        style={textareaStyle}
        value={jsonText}
        onChange={(e) => { setJsonText(e.target.value); setStatus(null) }}
        spellCheck={false}
        placeholder="Dán JSON vào đây..."
      />

      {status && (
        <div style={{
          padding: '0.625rem 0.875rem', borderRadius: '0.5rem', fontSize: '0.85rem',
          whiteSpace: 'pre-wrap', fontFamily: status.ok ? 'inherit' : 'monospace',
          background: status.ok ? '#d1e7dd' : 'rgba(180,19,64,0.08)',
          color: status.ok ? '#0a3622' : '#b41340',
          border: `1px solid ${status.ok ? '#198754' : 'rgba(180,19,64,0.25)'}`,
        }}>
          {status.msg}
        </div>
      )}

      <button
        className="btn-primary"
        onClick={handleImport}
        disabled={loading || !jsonText.trim()}
        style={{ alignSelf: 'flex-start' }}
      >
        {loading ? '⏳ Đang import...' : '📥 Import JSON'}
      </button>
    </div>
  )
}

// ─── Content Management Tab ───────────────────────────────────────────────────

function ContentTab() {
  const qc = useQueryClient()
  const [skill, setSkill] = useState('')
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)
  const [inputMode, setInputMode] = useState<'form' | 'json'>('form')
  const [form, setForm] = useState({
    title: '', skill: 'listening', cefrLevel: 'B1', levelLabel: 'B1', source: '',
    url: '', transcript: '', topicTags: '', difficultyScore: 5, examType: 'vstep',
  })

  const { data: exams } = useQuery({ queryKey: ['admin-exams'], queryFn: adminAPI.listExams })
  const levelOptions = levelOptionsFor(exams, form.examType)
  function changeContentExam(examType: string) {
    const opts = levelOptionsFor(exams, examType)
    const keep = opts.find((o) => o.cefr === form.cefrLevel) ?? opts[0]
    setForm((f) => ({ ...f, examType, cefrLevel: keep.cefr, levelLabel: keep.label }))
  }
  function changeContentLevel(cefr: string) {
    const opt = levelOptions.find((o) => o.cefr === cefr) ?? levelOptions[0]
    setForm((f) => ({ ...f, cefrLevel: opt.cefr, levelLabel: opt.label }))
  }

  const { data: content, isLoading } = useQuery({
    queryKey: ['admin-content', skill, page],
    queryFn: () => adminAPI.listContent({ skill: skill || undefined, page }),
  })

  const createContent = useMutation({
    mutationFn: () => adminAPI.createContent({
      title: form.title, skill: form.skill as any, cefrLevel: form.cefrLevel as any,
      source: form.source || undefined, url: form.url || undefined,
      transcript: form.transcript || undefined,
      topicTags: form.topicTags ? form.topicTags.split(',').map((t) => t.trim()) : undefined,
      difficultyScore: form.difficultyScore,
      examType: form.examType as any,
      levelLabel: form.levelLabel,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-content'] })
      setShowForm(false)
      setForm({ title: '', skill: 'listening', cefrLevel: 'B1', levelLabel: 'B1', source: '', url: '', transcript: '', topicTags: '', difficultyScore: 5, examType: 'vstep' })
    },
  })

  const deleteContent = useMutation({
    mutationFn: (id: string) => adminAPI.deleteContent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-content'] }),
  })

  return (
    <div className="admin-tab-panel">
      <div className="admin-toolbar">
        <select value={skill} onChange={(e) => { setSkill(e.target.value); setPage(1) }}>
          <option value="">Tất cả kỹ năng</option>
          {['listening', 'reading', 'writing', 'speaking'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Hủy' : '+ Thêm nội dung'}
        </button>
      </div>

      {showForm && (
        <div className="admin-form">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0 }}>Thêm nội dung mới</h3>
            <div style={{ display: 'flex', gap: '0.375rem' }}>
              <button
                onClick={() => setInputMode('form')}
                style={{ padding: '0.25rem 0.75rem', borderRadius: '1rem', border: '1px solid #004be2', cursor: 'pointer', fontSize: '0.8rem', fontWeight: inputMode === 'form' ? 700 : 400, background: inputMode === 'form' ? '#004be2' : 'white', color: inputMode === 'form' ? 'white' : '#004be2' }}
              >Form</button>
              <button
                onClick={() => setInputMode('json')}
                style={{ padding: '0.25rem 0.75rem', borderRadius: '1rem', border: '1px solid #004be2', cursor: 'pointer', fontSize: '0.8rem', fontWeight: inputMode === 'json' ? 700 : 400, background: inputMode === 'json' ? '#004be2' : 'white', color: inputMode === 'json' ? 'white' : '#004be2' }}
              >JSON</button>
            </div>
          </div>

          {inputMode === 'json' ? (
            <JsonImportPanel defaultType="content_items" onSuccess={() => { qc.invalidateQueries({ queryKey: ['admin-content'] }); setShowForm(false) }} />
          ) : (
            <>
              <div className="form-grid">
                <label>Tiêu đề *<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
                <label>Kỹ năng *
                  <select value={form.skill} onChange={(e) => setForm({ ...form, skill: e.target.value })}>
                    {['listening', 'reading', 'writing', 'speaking'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label>Kỳ thi
                  <select value={form.examType} onChange={(e) => changeContentExam(e.target.value)}>
                    <option value="vstep">VSTEP</option>
                    <option value="ielts_academic">IELTS Academic</option>
                    <option value="ielts_general">IELTS General</option>
                  </select>
                </label>
                <label>Cấp độ
                  <select value={form.cefrLevel} onChange={(e) => changeContentLevel(e.target.value)}>
                    {levelOptions.map((o) => <option key={o.cefr} value={o.cefr}>{o.label}</option>)}
                  </select>
                </label>
                <label>Nguồn<input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="BBC News" /></label>
                <label>URL<input type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></label>
                <label>Độ khó (1–10)<input type="number" min={1} max={10} value={form.difficultyScore} onChange={(e) => setForm({ ...form, difficultyScore: Number(e.target.value) })} /></label>
                <label>Tags (phẩy)<input value={form.topicTags} onChange={(e) => setForm({ ...form, topicTags: e.target.value })} /></label>
              </div>
              <label>Transcript<textarea rows={5} value={form.transcript} onChange={(e) => setForm({ ...form, transcript: e.target.value })} /></label>
              <button className="btn-primary" onClick={() => createContent.mutate()} disabled={!form.title || createContent.isPending}>
                {createContent.isPending ? 'Đang lưu...' : 'Lưu nội dung'}
              </button>
            </>
          )}
        </div>
      )}

      {isLoading ? <div className="loading">Đang tải...</div> : (
        <table className="admin-table">
          <thead><tr><th>Tiêu đề</th><th>Kỹ năng</th><th>Cấp độ</th><th>Nguồn</th><th>Ngày tạo</th><th></th></tr></thead>
          <tbody>
            {(content ?? []).map((row: any[]) => {
              const [id, source, title, , cefrLevel, skill] = row
              return (
                <tr key={id}>
                  <td className="table-title">{title}</td>
                  <td><span className={`skill-badge skill-${skill}`}>{skill}</span></td>
                  <td>{cefrLevel}</td><td>{source ?? '—'}</td>
                  <td>{row[12] ? new Date(row[12]).toLocaleDateString('vi') : '—'}</td>
                  <td><button className="btn-danger-sm" onClick={() => { if (confirm('Xóa?')) deleteContent.mutate(id) }}>Xóa</button></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
      <Pagination page={page} setPage={setPage} />
    </div>
  )
}

// ─── Question Bank Tab ────────────────────────────────────────────────────────

function QuestionsTab() {
  const qc = useQueryClient()
  const [skill, setSkill] = useState('')
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)
  const [inputMode, setInputMode] = useState<'form' | 'json'>('form')
  const [form, setForm] = useState({
    skill: 'reading', vstepPart: 'part2', questionType: 'mcq',
    prompt: '', options: '', correctAnswer: '', explanation: '', difficultyScore: 5,
  })

  const { data: questions, isLoading } = useQuery({
    queryKey: ['admin-questions', skill, page],
    queryFn: () => adminAPI.listQuestions({ skill: skill || undefined, page }),
  })

  const createQuestion = useMutation({
    mutationFn: () => adminAPI.createQuestion({
      skill: form.skill as any, vstepPart: form.vstepPart || undefined,
      questionType: form.questionType as any, prompt: form.prompt,
      options: form.options ? form.options.split('\n').filter(Boolean) : undefined,
      correctAnswer: form.correctAnswer, explanation: form.explanation || undefined,
      difficultyScore: form.difficultyScore,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-questions'] }); setShowForm(false) },
  })

  const deleteQuestion = useMutation({
    mutationFn: (id: string) => adminAPI.deleteQuestion(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-questions'] }),
  })

  return (
    <div className="admin-tab-panel">
      <div className="admin-toolbar">
        <select value={skill} onChange={(e) => { setSkill(e.target.value); setPage(1) }}>
          <option value="">Tất cả kỹ năng</option>
          {['listening', 'reading', 'writing', 'vocabulary', 'grammar'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>{showForm ? 'Hủy' : '+ Thêm câu hỏi'}</button>
      </div>

      {showForm && (
        <div className="admin-form">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0 }}>Thêm câu hỏi</h3>
            <div style={{ display: 'flex', gap: '0.375rem' }}>
              <button
                onClick={() => setInputMode('form')}
                style={{ padding: '0.25rem 0.75rem', borderRadius: '1rem', border: '1px solid #004be2', cursor: 'pointer', fontSize: '0.8rem', fontWeight: inputMode === 'form' ? 700 : 400, background: inputMode === 'form' ? '#004be2' : 'white', color: inputMode === 'form' ? 'white' : '#004be2' }}
              >Form</button>
              <button
                onClick={() => setInputMode('json')}
                style={{ padding: '0.25rem 0.75rem', borderRadius: '1rem', border: '1px solid #004be2', cursor: 'pointer', fontSize: '0.8rem', fontWeight: inputMode === 'json' ? 700 : 400, background: inputMode === 'json' ? '#004be2' : 'white', color: inputMode === 'json' ? 'white' : '#004be2' }}
              >JSON</button>
            </div>
          </div>

          {inputMode === 'json' ? (
            <JsonImportPanel defaultType="questions" onSuccess={() => { qc.invalidateQueries({ queryKey: ['admin-questions'] }); setShowForm(false) }} />
          ) : (
            <>
              <div className="form-grid">
                <label>Kỹ năng *
                  <select value={form.skill} onChange={(e) => setForm({ ...form, skill: e.target.value })}>
                    {['listening', 'reading', 'writing', 'vocabulary', 'grammar'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label>VSTEP Part<input value={form.vstepPart} onChange={(e) => setForm({ ...form, vstepPart: e.target.value })} /></label>
                <label>Dạng câu hỏi *
                  <select value={form.questionType} onChange={(e) => setForm({ ...form, questionType: e.target.value })}>
                    <option value="mcq">MCQ</option><option value="gap_fill">Gap Fill</option>
                    <option value="true_false">True/False</option><option value="short_answer">Short Answer</option>
                  </select>
                </label>
                <label>Độ khó (1–10)<input type="number" min={1} max={10} value={form.difficultyScore} onChange={(e) => setForm({ ...form, difficultyScore: Number(e.target.value) })} /></label>
              </div>
              <label>Câu hỏi *<textarea rows={3} value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} /></label>
              {form.questionType === 'mcq' && (
                <label>Các đáp án (mỗi dòng)<textarea rows={4} value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} /></label>
              )}
              <label>Đáp án đúng *<input value={form.correctAnswer} onChange={(e) => setForm({ ...form, correctAnswer: e.target.value })} /></label>
              <label>Giải thích<textarea rows={2} value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} /></label>
              <button className="btn-primary" onClick={() => createQuestion.mutate()} disabled={!form.prompt || !form.correctAnswer || createQuestion.isPending}>
                {createQuestion.isPending ? 'Đang lưu...' : 'Lưu câu hỏi'}
              </button>
            </>
          )}
        </div>
      )}

      {isLoading ? <div className="loading">Đang tải...</div> : (
        <table className="admin-table">
          <thead><tr><th>Câu hỏi</th><th>Kỹ năng</th><th>Dạng</th><th>Độ khó</th><th></th></tr></thead>
          <tbody>
            {(questions ?? []).map((row: any[]) => {
              const [id, skill, vstepPart, qType, prompt, , , , difficulty] = row
              return (
                <tr key={id}>
                  <td className="table-title">{String(prompt).slice(0, 80)}{String(prompt).length > 80 ? '...' : ''}</td>
                  <td><span className={`skill-badge skill-${skill}`}>{skill}</span>{vstepPart ? ` / ${vstepPart}` : ''}</td>
                  <td>{qType}</td><td>{difficulty}</td>
                  <td><button className="btn-danger-sm" onClick={() => { if (confirm('Xóa?')) deleteQuestion.mutate(id) }}>Xóa</button></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
      <Pagination page={page} setPage={setPage} />
    </div>
  )
}

// ─── Stats Tab ────────────────────────────────────────────────────────────────

function StatsTab() {
  const { data: stats, isLoading } = useQuery({ queryKey: ['admin-stats'], queryFn: adminAPI.getContentStats })
  if (isLoading) return <div className="loading">Đang tải thống kê...</div>
  if (!stats) return null
  return (
    <div className="admin-tab-panel">
      <h2>Thống kê nội dung</h2>
      <div className="stats-section">
        <h3>Nội dung theo kỹ năng & cấp độ</h3>
        <table className="admin-table">
          <thead><tr><th>Kỹ năng</th><th>Cấp độ</th><th>Tổng</th><th>Active</th></tr></thead>
          <tbody>{(stats.contentItems ?? []).map((row: any[], i: number) => (
            <tr key={i}><td>{row[0]}</td><td>{row[1]}</td><td>{row[2]}</td><td>{row[3]}</td></tr>
          ))}</tbody>
        </table>
      </div>
      <div className="stats-section">
        <h3>Ngân hàng câu hỏi</h3>
        <table className="admin-table">
          <thead><tr><th>Kỹ năng</th><th>Part</th><th>Tổng câu</th><th>Độ khó TB</th></tr></thead>
          <tbody>{(stats.questions ?? []).map((row: any[], i: number) => (
            <tr key={i}><td>{row[0]}</td><td>{row[1] ?? '—'}</td><td>{row[2]}</td><td>{Number(row[3]).toFixed(1)}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  )
}

// ─── AI Config Tab ────────────────────────────────────────────────────────────

function AIConfigTab() {
  const { data: configs, isLoading } = useQuery({ queryKey: ['admin-ai-config'], queryFn: adminAPI.getAIConfig })
  const { data: credit } = useQuery({ queryKey: ['admin-aws-credit'], queryFn: adminAPI.getAWSCredit })

  if (isLoading) return <div className="loading">Đang tải...</div>

  return (
    <div className="admin-tab-panel">
      <h2>Cấu hình AI Provider</h2>

      {credit && (
        <div className={`credit-card ${credit.isWarning ? 'warning' : ''}`}>
          <strong>AWS Credit: ${Number(credit.estimatedRemaining).toFixed(2)} còn lại</strong>
          <span> / ${credit.initialCredit} ban đầu</span>
          {credit.isWarning && <span className="badge-warning"> ⚠️ Gần hết — auto-switch khi &lt;${credit.autoSwitchThreshold}</span>}
        </div>
      )}

      {configs && Object.entries(configs).map(([module, cfg]: [string, any]) => (
        <div key={module} className="ai-config-row">
          <div className="ai-config-module">{module}</div>
          <div className="ai-config-details">
            <span className="ai-provider-badge">{cfg.provider}</span>
            {cfg.modelId && <span className="ai-model">{cfg.modelId}</span>}
            {cfg.fallbackProvider && <span className="ai-fallback">↳ fallback: {cfg.fallbackProvider}</span>}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Users Tab (FR-020) ───────────────────────────────────────────────────────

function UsersTab() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [subscription, setSubscription] = useState('')
  const [page, setPage] = useState(1)
  const [activeSearch, setActiveSearch] = useState('')
  const [auditTab, setAuditTab] = useState(false)

  const { data: users, isLoading } = useQuery({
    queryKey: ['admin-users', activeSearch, subscription, page],
    queryFn: () => adminAPI.listUsers({ search: activeSearch || undefined, subscription: subscription || undefined, page }),
  })

  const { data: inactive } = useQuery({
    queryKey: ['admin-users-inactive'],
    queryFn: () => adminAPI.getInactiveUsers(7),
  })

  const { data: auditLog } = useQuery({
    queryKey: ['admin-audit-log'],
    queryFn: adminAPI.getAuditLog,
    enabled: auditTab,
  })

  const lockUser = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => adminAPI.lockUser(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  const unlockUser = useMutation({
    mutationFn: (id: string) => adminAPI.unlockUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  const deleteUser = useMutation({
    mutationFn: (id: string) => adminAPI.deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  const grantPremium = useMutation({
    // Free tier is permanent — premium is a paid upgrade, no auto-expiry when granted manually
    mutationFn: (id: string) => adminAPI.updateUser(id, { subscriptionTier: 'premium' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  return (
    <div className="admin-tab-panel">
      <div className="admin-toolbar">
        <input
          placeholder="Tìm theo email, tên..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { setActiveSearch(search); setPage(1) } }}
        />
        <button className="btn-secondary" onClick={() => { setActiveSearch(search); setPage(1) }}>Tìm</button>
        <select value={subscription} onChange={(e) => { setSubscription(e.target.value); setPage(1) }}>
          <option value="">Tất cả gói</option>
          <option value="free">Free</option>
          <option value="premium">Premium</option>
        </select>
        <button className="btn-secondary" onClick={() => adminAPI.exportUsersCSV()}>📥 Xuất CSV</button>
        <button className={`btn-secondary ${auditTab ? 'active' : ''}`} onClick={() => setAuditTab(!auditTab)}>
          📋 Audit Log
        </button>
      </div>

      {inactive && (inactive as any[]).length > 0 && (
        <div className="alert-banner">
          ⚠️ {(inactive as any[]).length} người dùng inactive &gt; 7 ngày — cân nhắc gửi re-engagement email
        </div>
      )}

      {auditTab ? (
        <div>
          <h3>Audit Log</h3>
          <table className="admin-table">
            <thead><tr><th>Admin</th><th>Hành động</th><th>Mục tiêu</th><th>Chi tiết</th><th>Thời gian</th></tr></thead>
            <tbody>
              {(auditLog ?? []).map((row: any[], i: number) => (
                <tr key={i}>
                  <td>{row[1]}</td>
                  <td><span className="badge-action">{row[2]}</span></td>
                  <td>{row[3]}</td>
                  <td className="table-detail">{String(row[4] ?? '').slice(0, 60)}</td>
                  <td>{row[5] ? new Date(row[5]).toLocaleString('vi') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          {isLoading ? <div className="loading">Đang tải...</div> : (
            <table className="admin-table">
              <thead><tr><th>Email</th><th>Tên</th><th>Gói</th><th>Phiên học</th><th>Streak</th><th>Ngày đăng ký</th><th>Thao tác</th></tr></thead>
              <tbody>
                {(users ?? []).map((row: any[], i: number) => {
                  const [id, email, name, tier, createdAt, , totalSessions, streak] = row
                  return (
                    <tr key={i}>
                      <td>{email}</td>
                      <td>{name}</td>
                      <td><span className={`badge-tier ${tier}`}>{tier}</span></td>
                      <td>{totalSessions}</td>
                      <td>{streak}🔥</td>
                      <td>{createdAt ? new Date(createdAt).toLocaleDateString('vi') : '—'}</td>
                      <td className="action-cell">
                        {tier !== 'premium' && (
                          <button className="btn-xs" onClick={() => grantPremium.mutate(id)}>+Premium</button>
                        )}
                        <button className="btn-xs btn-warning" onClick={() => {
                          const reason = prompt('Lý do khóa:')
                          if (reason) lockUser.mutate({ id, reason })
                        }}>Khóa</button>
                        <button className="btn-xs btn-success" onClick={() => unlockUser.mutate(id)}>Mở</button>
                        <button className="btn-xs btn-danger" onClick={() => {
                          if (confirm('Xóa mềm tài khoản này? Dữ liệu học sẽ được giữ ẩn danh.') && confirm('Xác nhận lần 2?')) deleteUser.mutate(id)
                        }}>Xóa</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
          <Pagination page={page} setPage={setPage} />
        </>
      )}
    </div>
  )
}

// ─── System Monitor Tab (FR-023) ─────────────────────────────────────────────

function MonitorTab() {
  const { data: health } = useQuery({ queryKey: ['admin-health'], queryFn: adminAPI.getSystemHealth, refetchInterval: 30000 })
  const { data: costs } = useQuery({ queryKey: ['admin-ai-costs'], queryFn: () => adminAPI.getAICosts(30) })
  const { data: errors } = useQuery({ queryKey: ['admin-errors'], queryFn: adminAPI.getRuntimeErrors })
  const { data: aiLogs } = useQuery({ queryKey: ['admin-ai-logs'], queryFn: () => adminAPI.getAILogs({}) })
  const qc = useQueryClient()

  const resolveError = useMutation({
    mutationFn: (id: string) => adminAPI.resolveError(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-errors'] }),
  })

  return (
    <div className="admin-tab-panel">
      <h2>Giám sát hệ thống</h2>

      {health && (
        <div className="monitor-grid">
          <div className="monitor-card">
            <div className="monitor-label">Người dùng</div>
            <div className="monitor-value">{health.totalUsers?.toLocaleString()}</div>
          </div>
          <div className="monitor-card">
            <div className="monitor-label">Phiên học hôm nay</div>
            <div className="monitor-value">{health.sessionsToday}</div>
          </div>
          <div className="monitor-card">
            <div className="monitor-label">AI requests hôm nay</div>
            <div className="monitor-value">{health.aiRequestsToday}</div>
          </div>
          <div className="monitor-card">
            <div className="monitor-label">Chi phí AI hôm nay</div>
            <div className="monitor-value">${Number(health.aiCostToday ?? 0).toFixed(4)}</div>
          </div>
          <div className={`monitor-card ${Number(health.unresolvedErrors) > 0 ? 'monitor-error' : ''}`}>
            <div className="monitor-label">Lỗi chưa xử lý</div>
            <div className="monitor-value">{health.unresolvedErrors}</div>
          </div>
          <div className="monitor-card">
            <div className="monitor-label">Uptime</div>
            <div className="monitor-value">{Math.floor(Number(health.uptime) / 3600)}h</div>
          </div>
        </div>
      )}

      <div className="stats-section">
        <h3>Chi phí AI 30 ngày (theo module)</h3>
        <table className="admin-table">
          <thead><tr><th>Ngày</th><th>Module</th><th>Provider</th><th>Requests</th><th>Chi phí ($)</th></tr></thead>
          <tbody>
            {(costs ?? []).slice(0, 20).map((row: any[], i: number) => (
              <tr key={i}>
                <td>{row[0] ? new Date(row[0]).toLocaleDateString('vi') : '—'}</td>
                <td>{row[1]}</td><td>{row[2]}</td><td>{row[3]}</td>
                <td>${Number(row[6] ?? 0).toFixed(5)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="stats-section">
        <h3>Lỗi runtime {errors && (errors as any[]).filter((r: any[]) => !r[6]).length > 0 && <span className="badge-error">!</span>}</h3>
        {(errors ?? []).length === 0 ? <p className="empty-hint">Không có lỗi nào.</p> : (
          <table className="admin-table">
            <thead><tr><th>Thông báo lỗi</th><th>Endpoint</th><th>Số lần</th><th>Lần cuối</th><th>Trạng thái</th><th></th></tr></thead>
            <tbody>
              {(errors as any[]).map((row: any[], i: number) => {
                const [id, message, endpoint, errorCount, , isResolved, , lastSeen] = row
                return (
                  <tr key={i} className={isResolved ? 'resolved-row' : ''}>
                    <td className="table-title">{String(message).slice(0, 80)}</td>
                    <td>{endpoint}</td><td>{errorCount}</td>
                    <td>{lastSeen ? new Date(lastSeen).toLocaleString('vi') : '—'}</td>
                    <td>{isResolved ? '✅ Đã xử lý' : '🔴 Chưa xử lý'}</td>
                    <td>{!isResolved && <button className="btn-xs btn-success" onClick={() => resolveError.mutate(id)}>Đánh dấu xong</button>}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="stats-section">
        <h3>AI Request Logs gần nhất</h3>
        <table className="admin-table">
          <thead><tr><th>Module</th><th>Provider</th><th>Model</th><th>Tokens IN</th><th>Tokens OUT</th><th>Chi phí</th><th>Latency</th><th>Thời gian</th></tr></thead>
          <tbody>
            {(aiLogs ?? []).slice(0, 15).map((row: any[], i: number) => (
              <tr key={i}>
                <td>{row[2]}</td><td>{row[3]}</td><td>{row[4]}</td>
                <td>{row[5]}</td><td>{row[6]}</td>
                <td>${Number(row[7] ?? 0).toFixed(6)}</td>
                <td>{row[8]}ms</td>
                <td>{row[11] ? new Date(row[11]).toLocaleString('vi') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── System Config Tab (FR-024) ──────────────────────────────────────────────

function SystemConfigTab() {
  const qc = useQueryClient()
  const [configSection, setConfigSection] = useState<'flags' | 'maintenance' | 'limits' | 'security' | 'teachers'>('flags')

  const { data: flags } = useQuery({ queryKey: ['admin-flags'], queryFn: adminAPI.getFeatureFlags, enabled: configSection === 'flags' })
  const { data: maintenance } = useQuery({ queryKey: ['admin-maintenance'], queryFn: adminAPI.getMaintenance, enabled: configSection === 'maintenance' })
  const { data: limits } = useQuery({ queryKey: ['admin-limits'], queryFn: adminAPI.getFreeLimits, enabled: configSection === 'limits' })
  const { data: security } = useQuery({ queryKey: ['admin-security'], queryFn: adminAPI.getSecurityConfig, enabled: configSection === 'security' })
  const { data: teachers } = useQuery({ queryKey: ['admin-teachers'], queryFn: adminAPI.getTeacherWhitelist, enabled: configSection === 'teachers' })

  const updateFlag = useMutation({
    mutationFn: ({ flag, isEnabled, rolloutPercent }: any) => adminAPI.updateFeatureFlag(flag, { isEnabled, rolloutPercent }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-flags'] }),
  })

  const createFlag = useMutation({
    mutationFn: ({ flag, description }: any) => adminAPI.createFeatureFlag(flag, description),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-flags'] }),
  })

  const [newFlag, setNewFlag] = useState({ flag: '', description: '' })
  const [maintenanceForm, setMaintenanceForm] = useState({ enabled: false, message: '' })
  const [limitsForm, setLimitsForm] = useState<Record<string, number>>({})

  return (
    <div className="admin-tab-panel">
      <h2>Cấu hình hệ thống</h2>
      <div className="config-sections">
        {(['flags', 'maintenance', 'limits', 'security', 'teachers'] as const).map((s) => (
          <button key={s} className={`btn-secondary ${configSection === s ? 'active' : ''}`} onClick={() => setConfigSection(s)}>
            {s === 'flags' ? 'Feature Flags' : s === 'maintenance' ? 'Bảo trì' : s === 'limits' ? 'Giới hạn' : s === 'security' ? 'Bảo mật' : 'Whitelist GV'}
          </button>
        ))}
      </div>

      {configSection === 'flags' && (
        <div>
          <div className="admin-form" style={{ marginBottom: 16 }}>
            <h4>Tạo flag mới</h4>
            <div className="form-grid">
              <label>Flag key<input value={newFlag.flag} onChange={(e) => setNewFlag({ ...newFlag, flag: e.target.value })} placeholder="feature_name" /></label>
              <label>Mô tả<input value={newFlag.description} onChange={(e) => setNewFlag({ ...newFlag, description: e.target.value })} /></label>
            </div>
            <button className="btn-primary" onClick={() => { createFlag.mutate(newFlag); setNewFlag({ flag: '', description: '' }) }} disabled={!newFlag.flag}>
              Tạo Flag
            </button>
          </div>
          <table className="admin-table">
            <thead><tr><th>Flag</th><th>Mô tả</th><th>Bật/Tắt</th><th>Rollout %</th></tr></thead>
            <tbody>
              {(flags ?? []).map((row: any[], i: number) => {
                const [flag, isEnabled, rollout, description] = row
                return (
                  <tr key={i}>
                    <td><code>{flag}</code></td>
                    <td>{description}</td>
                    <td>
                      <button className={`toggle-btn ${isEnabled ? 'on' : 'off'}`}
                        onClick={() => updateFlag.mutate({ flag, isEnabled: !isEnabled })}>
                        {isEnabled ? 'BẬT' : 'TẮT'}
                      </button>
                    </td>
                    <td>
                      <input type="range" min={0} max={100} value={rollout}
                        onChange={(e) => updateFlag.mutate({ flag, rolloutPercent: Number(e.target.value) })} />
                      <span>{rollout}%</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {configSection === 'maintenance' && maintenance && (
        <div className="admin-form">
          <h4>Chế độ bảo trì</h4>
          <label className="toggle-row">
            <span>Bật bảo trì</span>
            <input type="checkbox" defaultChecked={maintenance.enabled}
              onChange={(e) => setMaintenanceForm({ ...maintenanceForm, enabled: e.target.checked })} />
          </label>
          <label>Thông báo hiển thị cho user
            <textarea rows={3} defaultValue={maintenance.message}
              onChange={(e) => setMaintenanceForm({ ...maintenanceForm, message: e.target.value })} />
          </label>
          <button className="btn-primary" onClick={() => adminAPI.setMaintenance(maintenanceForm.enabled, maintenanceForm.message)
            .then(() => qc.invalidateQueries({ queryKey: ['admin-maintenance'] }))}>
            Lưu cài đặt bảo trì
          </button>
          {maintenance.enabled && <div className="badge-warning">⚠️ Hệ thống đang ở chế độ bảo trì</div>}
        </div>
      )}

      {configSection === 'limits' && limits && (
        <div className="admin-form">
          <h4>Giới hạn tính năng Free Tier</h4>
          <p className="admin-hint">Free tier <strong>không có thời hạn</strong> — người dùng dùng miễn phí mãi mãi trong phạm vi giới hạn dưới đây. Premium mở khóa không giới hạn.</p>
          {Object.entries(limits as Record<string, { value: number; description: string }>).map(([key, cfg]) => (
            <label key={key}>{cfg.description}
              <input type="number" min={0} defaultValue={cfg.value}
                onChange={(e) => setLimitsForm({ ...limitsForm, [key]: Number(e.target.value) })} />
            </label>
          ))}
          <button className="btn-primary" onClick={() => adminAPI.updateFreeLimits(limitsForm)
            .then(() => qc.invalidateQueries({ queryKey: ['admin-limits'] }))}>
            Cập nhật giới hạn
          </button>
        </div>
      )}

      {configSection === 'security' && security && (
        <div className="admin-form">
          <h4>Cấu hình bảo mật</h4>
          {Object.entries(security as Record<string, { value: string; description: string }>).map(([key, cfg]) => (
            <label key={key}>{cfg.description}
              <input defaultValue={cfg.value} />
            </label>
          ))}
          <p className="admin-hint">⚠️ Thay đổi cài đặt bảo mật có hiệu lực sau khi restart server.</p>
        </div>
      )}

      {configSection === 'teachers' && (
        <div>
          <h4>Whitelist giáo viên</h4>
          <p className="admin-hint">Danh sách email được cấp quyền TEACHER. Hiện quản lý qua biến môi trường TEACHER_EMAILS.</p>
          <ul className="teacher-list">
            {(teachers ?? []).map((email: string, i: number) => (
              <li key={i}>{email}</li>
            ))}
          </ul>
          {(teachers ?? []).length === 0 && <p className="empty-hint">Chưa có giáo viên nào. Thêm vào TEACHER_EMAILS env.</p>}
        </div>
      )}
    </div>
  )
}

// ─── Gen Item Preview (shared) ───────────────────────────────────────────────

function GenItemPreview({ parsed, contentType }: { parsed: any; contentType?: string }) {
  if (!parsed || typeof parsed !== 'object') return null

  switch (contentType) {
    case 'reading_passage':
      return (
        <div>
          {parsed.title && <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{parsed.title}</strong>}
          {parsed.passage
            ? <p style={{ margin: 0, color: '#535b71', fontSize: '0.875rem' }}>{String(parsed.passage).slice(0, 250)}…</p>
            : <p style={{ margin: 0, color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Không có nội dung passage</p>
          }
          {parsed.questions?.length > 0 && <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#a5adc6' }}>{parsed.questions.length} câu hỏi</p>}
        </div>
      )
    case 'listening_script':
      return (
        <div>
          {parsed.title && <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{parsed.title}</strong>}
          {parsed.script
            ? <p style={{ margin: 0, color: '#535b71', fontSize: '0.875rem' }}>{String(parsed.script).slice(0, 250)}…</p>
            : <p style={{ margin: 0, color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Không có script</p>
          }
          {parsed.questions?.length > 0 && <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#a5adc6' }}>{parsed.questions.length} câu hỏi</p>}
        </div>
      )
    case 'writing_prompt':
      return (
        <div>
          {parsed.task_type && <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: '#004be2', marginBottom: '0.25rem', display: 'block' }}>{parsed.task_type}</span>}
          {parsed.prompt
            ? <p style={{ margin: 0, color: '#535b71', fontSize: '0.875rem' }}>{String(parsed.prompt).slice(0, 250)}</p>
            : <p style={{ margin: 0, color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Không có prompt</p>
          }
          {parsed.word_count > 0 && <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#a5adc6' }}>~{parsed.word_count} từ</p>}
        </div>
      )
    case 'speaking_prompt':
      return (
        <div>
          {parsed.part && <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: '#006571', marginBottom: '0.25rem', display: 'block' }}>{parsed.part}</span>}
          {parsed.topic && <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{parsed.topic}</strong>}
          {parsed.outline_hints?.length > 0 && <p style={{ margin: 0, color: '#535b71', fontSize: '0.875rem' }}>{(parsed.outline_hints as string[]).slice(0, 3).join(' · ')}</p>}
          {!parsed.topic && !parsed.outline_hints?.length && <p style={{ margin: 0, color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Không có nội dung</p>}
        </div>
      )
    case 'grammar_drill':
      return (
        <div>
          {parsed.topic && <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{parsed.topic}</strong>}
          {parsed.questions?.length > 0
            ? (parsed.questions as any[]).slice(0, 2).map((q: any, i: number) => (
                <p key={i} style={{ margin: '0.125rem 0', color: '#535b71', fontSize: '0.875rem' }}>{i + 1}. {String(q.prompt ?? '').slice(0, 120)}</p>
              ))
            : <p style={{ margin: 0, color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Không có câu hỏi</p>
          }
          {parsed.questions?.length > 2 && <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#a5adc6' }}>+{parsed.questions.length - 2} câu nữa</p>}
        </div>
      )
    case 'vocabulary_drill':
      return (
        <div>
          {parsed.words?.length > 0
            ? <>
                {(parsed.words as any[]).slice(0, 5).map((w: any, i: number) => (
                  <span key={i} style={{ display: 'inline-block', margin: '0.125rem', padding: '0.125rem 0.5rem', background: '#eef0ff', borderRadius: '9999px', fontSize: '0.8125rem', fontWeight: 600, color: '#004be2' }}>{w.word}</span>
                ))}
                {parsed.words.length > 5 && <span style={{ fontSize: '0.75rem', color: '#a5adc6' }}> +{parsed.words.length - 5} từ</span>}
              </>
            : <p style={{ margin: 0, color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Không có từ vựng</p>
          }
        </div>
      )
    default:
      return <p style={{ margin: 0, color: '#535b71', fontSize: '0.875rem' }}>{parsed.title ?? parsed.prompt ?? parsed.topic ?? contentType ?? '—'}</p>
  }
}

// ─── AI Content Generator Tab (FR-019) ───────────────────────────────────────

// Level options on the exam's native scale (IELTS band vs VSTEP CEFR), derived
// from the exam_frameworks registry. Falls back to raw CEFR before exams load.
const FALLBACK_LEVELS: LevelOption[] = ['A2', 'B1', 'B2', 'C1'].map((c) => ({
  cefr: c as LevelOption['cefr'], label: c, difficulty: biasedDifficultyRange(c),
}))
function levelOptionsFor(exams: any[] | undefined, examType: string): LevelOption[] {
  const fw = exams?.find((e) => e.examType === examType)
  if (!fw) return FALLBACK_LEVELS
  let mapping: Record<string, string> = {}
  try { mapping = JSON.parse(fw.scoreMappingJson || '{}') } catch { mapping = {} }
  const opts = listLevelOptions(fw.scoreScale, mapping)
  return opts.length ? opts : FALLBACK_LEVELS
}

// Spread the chosen difficulty within the level so the batch holds easy→hard items.
const DIFFICULTY_BIAS_LABELS: Record<string, string> = {
  full: 'Toàn dải (dễ → khó)', easy: 'Dễ', medium: 'Vừa', hard: 'Khó',
}

function ContentGeneratorTab() {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    contentType: 'reading_passage',
    cefrLevel: 'B1',
    levelLabel: 'B1',
    difficultyBias: 'full',
    topic: '',
    quantity: 5,
    examType: 'vstep',
  })

  const { data: exams } = useQuery({ queryKey: ['admin-exams'], queryFn: adminAPI.listExams })
  const levelOptions = levelOptionsFor(exams, form.examType)

  // When the exam changes, the level scale changes too — snap to a valid option.
  function changeExam(examType: string) {
    const opts = levelOptionsFor(exams, examType)
    const keep = opts.find((o) => o.cefr === form.cefrLevel) ?? opts[0]
    setForm((f) => ({ ...f, examType, cefrLevel: keep.cefr, levelLabel: keep.label }))
  }
  function changeLevel(cefr: string) {
    const opt = levelOptions.find((o) => o.cefr === cefr) ?? levelOptions[0]
    setForm((f) => ({ ...f, cefrLevel: opt.cefr, levelLabel: opt.label }))
  }
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [genError, setGenError] = useState<string | null>(null)

  const { data: history } = useQuery({ queryKey: ['admin-gen-history'], queryFn: adminAPI.getContentGenHistory })

  const { data: activeJob, isLoading: jobLoading } = useQuery({
    queryKey: ['admin-gen-job', activeJobId],
    queryFn: () => adminAPI.getContentGenJob(activeJobId!),
    enabled: !!activeJobId,
    refetchInterval: (query) => {
      const job = (query.state.data as any)?.job
      return job && (job[4] === 'DONE' || job[4] === 'FAILED') ? false : 3000
    },
  })

  const startGeneration = useMutation({
    mutationFn: () => {
      const [difficultyMin, difficultyMax] = biasedDifficultyRange(
        form.cefrLevel, form.difficultyBias === 'full' ? undefined : (form.difficultyBias as 'easy' | 'medium' | 'hard'),
      )
      const { difficultyBias: _bias, ...rest } = form
      return adminAPI.startContentGeneration({ ...rest, difficultyMin, difficultyMax })
    },
    onSuccess: (data: any) => {
      setGenError(null)
      const jobId = data?.data?.jobId ?? data?.jobId
      setActiveJobId(jobId)
      qc.invalidateQueries({ queryKey: ['admin-gen-history'] })
    },
    onError: (err: any) => {
      setGenError(err?.message ?? 'Lỗi không xác định khi tạo nội dung')
    },
  })

  const approveAll = useMutation({
    mutationFn: (jobId: string) => adminAPI.approveAllItems(jobId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-gen-job', activeJobId] }),
  })

  const approveItem = useMutation({
    mutationFn: (itemId: string) => adminAPI.approveItem(itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-gen-job', activeJobId] }),
  })

  const rejectItem = useMutation({
    mutationFn: (itemId: string) => adminAPI.rejectItem(itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-gen-job', activeJobId] }),
  })

  const regenerateItem = useMutation({
    mutationFn: (itemId: string) => adminAPI.regenerateItem(itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-gen-job', activeJobId] }),
  })

  const estimatedCost = (form.quantity * 0.0003).toFixed(4)

  const contentTypeLabels: Record<string, string> = {
    reading_passage: 'Reading Passage',
    listening_script: 'Listening Script',
    writing_prompt: 'Writing Prompt',
    vocabulary_drill: 'Vocabulary Drill',
    grammar_drill: 'Grammar Drill',
    speaking_prompt: 'Speaking Prompt',
  }

  return (
    <div className="admin-tab-panel">
      <h2>AI Content Generator (FR-019)</h2>

      <div className="admin-form">
        <h3>Tạo nội dung hàng loạt</h3>
        <div className="form-grid">
          <label>Loại nội dung
            <select value={form.contentType} onChange={(e) => setForm({ ...form, contentType: e.target.value })}>
              {Object.entries(contentTypeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label>Kỳ thi
            <select value={form.examType} onChange={(e) => changeExam(e.target.value)}>
              <option value="vstep">VSTEP</option>
              <option value="ielts_academic">IELTS Academic</option>
              <option value="ielts_general">IELTS General</option>
            </select>
          </label>
          <label>Cấp độ
            <select value={form.cefrLevel} onChange={(e) => changeLevel(e.target.value)}>
              {levelOptions.map((o) => <option key={o.cefr} value={o.cefr}>{o.label}</option>)}
            </select>
          </label>
          <label>Độ khó trong cấp
            <select value={form.difficultyBias} onChange={(e) => setForm({ ...form, difficultyBias: e.target.value })}>
              {Object.entries(DIFFICULTY_BIAS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label>Chủ đề (tùy chọn)
            <input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="environment, technology..." />
          </label>
          <label>Số lượng (1–50)
            <input type="range" min={1} max={50} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} />
            <span className="range-value">{form.quantity} items</span>
          </label>
        </div>
        <div className="cost-estimate">Ước tính chi phí: ~${estimatedCost} ({form.quantity} items × Qwen)</div>
        {form.contentType === 'writing_prompt' && form.examType === 'ielts_academic' && (
          <div style={{ marginTop: '0.5rem', padding: '0.625rem 0.875rem', background: 'rgba(0,75,226,0.06)', border: '1px solid rgba(0,75,226,0.2)', borderRadius: '0.625rem', color: '#004be2', fontSize: '0.8125rem', fontWeight: 600 }}>
            📊 AI sẽ tạo đề <strong>Writing Task 1</strong> kèm biểu đồ (figure). Sau khi duyệt, đề hiển thị ở trang Writing khi học viên chọn kỳ thi <strong>IELTS Academic</strong>.
          </div>
        )}
        <button className="btn-primary" onClick={() => startGeneration.mutate()} disabled={startGeneration.isPending}>
          {startGeneration.isPending ? '⏳ Đang khởi tạo...' : '🚀 Tạo nội dung'}
        </button>
        {genError && (
          <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', background: 'rgba(180,19,64,0.08)', border: '1px solid rgba(180,19,64,0.25)', borderRadius: '0.75rem', color: '#b41340', fontSize: '0.875rem', fontWeight: 600 }}>
            ❌ {genError}
          </div>
        )}
      </div>

      {activeJobId && (
        <div className="gen-job-panel">
          {jobLoading ? <div className="loading">Đang tải job...</div> : activeJob && (() => {
            const job = (activeJob as any).job
            const items = (activeJob as any).items ?? []
            const [_jobId, _contentType, _cefrLevel, _topic, _quantity, status] = job ?? []
            const pending = items.filter((r: any[]) => r[5] === 'PENDING_REVIEW').length
            const approved = items.filter((r: any[]) => r[5] === 'APPROVED').length

            return (
              <div>
                <div className="job-status-bar">
                  <strong>Job #{activeJobId.slice(0, 8)}</strong>
                  <span className={`job-status ${status?.toLowerCase()}`}>{status}</span>
                  <span>{approved}/{items.length} đã duyệt</span>
                  {status === 'DONE' && pending > 0 && (
                    <button className="btn-primary" onClick={() => approveAll.mutate(activeJobId)}>
                      ✅ Duyệt tất cả ({pending} còn lại)
                    </button>
                  )}
                </div>

                <div className="gen-items-list">
                  {items.map((row: any[], i: number) => {
                    const [itemId, , cefrLevel, topic, payload, itemStatus] = row
                    const safeStatus = String(itemStatus ?? 'pending').toLowerCase()
                    let parsed: any = {}
                    try { parsed = JSON.parse(payload) } catch {}

                    return (
                      <div key={i} className={`gen-item ${safeStatus}`}>
                        <div className="gen-item-header">
                          <span>#{i + 1} — {cefrLevel}{topic ? ` / ${topic}` : ''}</span>
                          <span className={`item-status ${safeStatus}`}>{itemStatus}</span>
                        </div>
                        <div className="gen-item-preview">
                          <GenItemPreview parsed={parsed} contentType={row[1]} />
                        </div>
                        {itemStatus === 'PENDING_REVIEW' && (
                          <div className="gen-item-actions">
                            <button className="btn-xs btn-success" onClick={() => approveItem.mutate(itemId)}>✅ Duyệt</button>
                            <button className="btn-xs btn-danger" onClick={() => rejectItem.mutate(itemId)}>❌ Từ chối</button>
                            <button className="btn-xs" onClick={() => regenerateItem.mutate(itemId)}>🔄 Tạo lại</button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })()}
        </div>
      )}

      <div className="stats-section">
        <h3>Lịch sử batch generation</h3>
        <table className="admin-table">
          <thead><tr><th>Loại</th><th>CEFR</th><th>Chủ đề</th><th>Số lượng</th><th>Đã duyệt</th><th>Chi phí</th><th>Trạng thái</th><th>Ngày tạo</th><th></th></tr></thead>
          <tbody>
            {(history ?? []).map((row: any[], i: number) => {
              const [jobId, contentType, cefrLevel, topic, quantity, status, , itemsApproved, aiCost, createdAt] = row
              return (
                <tr key={i}>
                  <td>{contentTypeLabels[contentType] ?? contentType}</td>
                  <td>{cefrLevel}</td><td>{topic ?? '—'}</td>
                  <td>{quantity}</td><td>{itemsApproved ?? 0}</td>
                  <td>${Number(aiCost ?? 0).toFixed(5)}</td>
                  <td><span className={`job-status ${status?.toLowerCase()}`}>{status}</span></td>
                  <td>{createdAt ? new Date(createdAt).toLocaleDateString('vi') : '—'}</td>
                  <td><button className="btn-xs" onClick={() => setActiveJobId(jobId)}>Xem</button></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── FR-021: Revenue Tab ──────────────────────────────────────────────────────

function RevenueTab() {
  const [days, setDays] = useState(30)
  const [couponForm, setCouponForm] = useState({ code: '', discountType: 'percent', discountValue: 20, maxUses: '' })
  const qc = useQueryClient()

  const { data: summary } = useQuery({ queryKey: ['revenue-summary', days], queryFn: () => adminAPI.getRevenueSummary(days) })
  const { data: coupons } = useQuery({ queryKey: ['coupons'], queryFn: () => adminAPI.getCoupons() })
  const { data: transactions } = useQuery({ queryKey: ['transactions'], queryFn: () => adminAPI.getTransactions() })

  const createCoupon = useMutation({
    mutationFn: () => adminAPI.createCoupon({ ...couponForm, discountValue: Number(couponForm.discountValue), maxUses: couponForm.maxUses ? Number(couponForm.maxUses) : undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['coupons'] }); setCouponForm({ code: '', discountType: 'percent', discountValue: 20, maxUses: '' }) },
  })
  const toggleCoupon = useMutation({ mutationFn: (code: string) => adminAPI.toggleCoupon(code), onSuccess: () => qc.invalidateQueries({ queryKey: ['coupons'] }) })

  const s = summary
  return (
    <div className="admin-tab-panel">
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Doanh thu</h3>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ marginLeft: 'auto' }}>
          {[7, 30, 90].map((d) => <option key={d} value={d}>{d} ngày</option>)}
        </select>
        <button className="btn-secondary" onClick={() => adminAPI.exportTransactionsCSV()}>Export CSV</button>
      </div>

      {s && (
        <div className="monitor-grid" style={{ marginBottom: 20 }}>
          <div className="monitor-card"><div className="monitor-label">Giao dịch</div><div className="monitor-value">{s.totalTransactions}</div></div>
          <div className="monitor-card"><div className="monitor-label">Doanh thu (VND)</div><div className="monitor-value">{Number(s.totalRevenueVnd ?? 0).toLocaleString('vi-VN')}</div></div>
          <div className="monitor-card monitor-error"><div className="monitor-label">Hoàn tiền</div><div className="monitor-value">{Number(s.totalRefundedVnd ?? 0).toLocaleString('vi-VN')}</div></div>
        </div>
      )}

      <div className="stats-section">
        <h4>Gói đăng ký</h4>
        <table className="admin-table">
          <thead><tr><th>Gói</th><th>Số lượng</th></tr></thead>
          <tbody>{((s?.subscriptionCounts as any[]) ?? []).map((r: any, i: number) => (
            <tr key={i}><td>{r[0]}</td><td>{r[1]}</td></tr>
          ))}</tbody>
        </table>
      </div>

      <div className="stats-section" style={{ marginTop: 24 }}>
        <h4>Giao dịch gần đây</h4>
        <table className="admin-table">
          <thead><tr><th>Email</th><th>Gói</th><th>Số tiền</th><th>Trạng thái</th><th>Gateway</th><th>Ngày</th></tr></thead>
          <tbody>{((transactions as any[]) ?? []).slice(0, 20).map((r: any, i: number) => (
            <tr key={i}><td>{r[1]}</td><td>{r[2]}</td><td>{Number(r[3]).toLocaleString('vi-VN')} VND</td>
              <td><span className={`job-status ${r[4]}`}>{r[4]}</span></td><td>{r[5]}</td>
              <td>{r[8] ? new Date(r[8]).toLocaleDateString('vi-VN') : ''}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      <div className="stats-section" style={{ marginTop: 24 }}>
        <h4>Quản lý Coupon</h4>
        <div className="admin-form" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <input className="admin-input" placeholder="Mã coupon" value={couponForm.code} onChange={(e) => setCouponForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} />
          <select className="admin-input" value={couponForm.discountType} onChange={(e) => setCouponForm((f) => ({ ...f, discountType: e.target.value }))} style={{ width: 120 }}>
            <option value="percent">%</option><option value="fixed">Cố định</option>
          </select>
          <input className="admin-input" type="number" placeholder="Giá trị" style={{ width: 80 }} value={couponForm.discountValue} onChange={(e) => setCouponForm((f) => ({ ...f, discountValue: Number(e.target.value) }))} />
          <input className="admin-input" type="number" placeholder="Max uses (để trống = ∞)" style={{ width: 140 }} value={couponForm.maxUses} onChange={(e) => setCouponForm((f) => ({ ...f, maxUses: e.target.value }))} />
          <button className="btn-primary" onClick={() => createCoupon.mutate()} disabled={!couponForm.code}>Tạo coupon</button>
        </div>
        <table className="admin-table">
          <thead><tr><th>Code</th><th>Loại</th><th>Giá trị</th><th>Đã dùng / Max</th><th>Trạng thái</th><th></th></tr></thead>
          <tbody>{((coupons as any[]) ?? []).map((r: any, i: number) => (
            <tr key={i}>
              <td><strong>{r[0]}</strong></td><td>{r[1]}</td><td>{r[2]}{r[1] === 'percent' ? '%' : ' VND'}</td>
              <td>{r[4]} / {r[3] ?? '∞'}</td>
              <td><span className={`job-status ${r[8] ? 'approved' : 'failed'}`}>{r[8] ? 'Active' : 'Off'}</span></td>
              <td className="action-cell">
                <button className="btn-xs btn-warning" onClick={() => toggleCoupon.mutate(r[0])}>Toggle</button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  )
}

// ─── FR-022: Notifications Tab ────────────────────────────────────────────────

function NotificationsTab() {
  const [broadcastForm, setBroadcastForm] = useState({ title: '', body: '', targetGroup: 'all', channel: 'in_app' })
  const [bannerForm, setBannerForm] = useState({ content: '', bgColor: '#1e40af', ctaText: '', ctaUrl: '' })
  const qc = useQueryClient()

  const { data: broadcasts } = useQuery({ queryKey: ['broadcasts'], queryFn: () => adminAPI.getBroadcasts() })
  const { data: banners } = useQuery({ queryKey: ['banners'], queryFn: () => adminAPI.getBanners() })

  const sendBroadcast = useMutation({
    mutationFn: () => adminAPI.sendBroadcast(broadcastForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['broadcasts'] }); setBroadcastForm({ title: '', body: '', targetGroup: 'all', channel: 'in_app' }) },
  })
  const createBanner = useMutation({
    mutationFn: () => adminAPI.createBanner(bannerForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['banners'] }); setBannerForm({ content: '', bgColor: '#1e40af', ctaText: '', ctaUrl: '' }) },
  })
  const toggleBanner = useMutation({ mutationFn: (id: string) => adminAPI.toggleBanner(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['banners'] }) })
  const deleteBanner = useMutation({ mutationFn: (id: string) => adminAPI.deleteBanner(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['banners'] }) })

  return (
    <div className="admin-tab-panel">
      <div className="stats-section">
        <h3>Gửi thông báo</h3>
        <div className="admin-form" style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 500, marginBottom: 16 }}>
          <input className="admin-input" placeholder="Tiêu đề" value={broadcastForm.title} onChange={(e) => setBroadcastForm((f) => ({ ...f, title: e.target.value }))} />
          <textarea className="admin-input" rows={3} placeholder="Nội dung thông báo" value={broadcastForm.body} onChange={(e) => setBroadcastForm((f) => ({ ...f, body: e.target.value }))} style={{ resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <select className="admin-input" value={broadcastForm.targetGroup} onChange={(e) => setBroadcastForm((f) => ({ ...f, targetGroup: e.target.value }))}>
              <option value="all">Tất cả</option><option value="free">Free</option>
              <option value="premium">Premium</option><option value="inactive">Không hoạt động 14 ngày</option>
            </select>
            <select className="admin-input" value={broadcastForm.channel} onChange={(e) => setBroadcastForm((f) => ({ ...f, channel: e.target.value }))}>
              <option value="in_app">In-app</option><option value="email">Email</option><option value="push">Push</option>
            </select>
          </div>
          <button className="btn-primary" onClick={() => sendBroadcast.mutate()} disabled={!broadcastForm.title || !broadcastForm.body || sendBroadcast.isPending}>
            {sendBroadcast.isPending ? 'Đang gửi...' : 'Gửi thông báo'}
          </button>
        </div>
        <h4>Lịch sử gửi</h4>
        <table className="admin-table">
          <thead><tr><th>Tiêu đề</th><th>Nhóm</th><th>Kênh</th><th>Người nhận</th><th>Trạng thái</th><th>Ngày</th></tr></thead>
          <tbody>{((broadcasts as any[]) ?? []).map((r: any, i: number) => (
            <tr key={i}><td>{r[2]}</td><td>{r[1]}</td><td>{r[3]}</td><td>{r[4]}</td>
              <td><span className={`job-status ${r[8]?.toLowerCase()}`}>{r[8]}</span></td>
              <td>{r[9] ? new Date(r[9]).toLocaleDateString('vi-VN') : ''}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      <div className="stats-section" style={{ marginTop: 24 }}>
        <h3>Banner thông báo</h3>
        <div className="admin-form" style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 500, marginBottom: 16 }}>
          <input className="admin-input" placeholder="Nội dung banner" value={bannerForm.content} onChange={(e) => setBannerForm((f) => ({ ...f, content: e.target.value }))} />
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="admin-input" type="color" value={bannerForm.bgColor} onChange={(e) => setBannerForm((f) => ({ ...f, bgColor: e.target.value }))} style={{ width: 50, padding: 2 }} />
            <input className="admin-input" placeholder="CTA text (VD: Tìm hiểu thêm)" value={bannerForm.ctaText} onChange={(e) => setBannerForm((f) => ({ ...f, ctaText: e.target.value }))} />
            <input className="admin-input" placeholder="CTA URL" value={bannerForm.ctaUrl} onChange={(e) => setBannerForm((f) => ({ ...f, ctaUrl: e.target.value }))} />
          </div>
          <button className="btn-primary" onClick={() => createBanner.mutate()} disabled={!bannerForm.content}>Tạo banner</button>
        </div>
        <table className="admin-table">
          <thead><tr><th>Nội dung</th><th>Trạng thái</th><th></th></tr></thead>
          <tbody>{((banners as any[]) ?? []).map((r: any, i: number) => (
            <tr key={i}>
              <td style={{ maxWidth: 300 }}>{String(r[1]).slice(0, 80)}</td>
              <td><span className={`job-status ${r[5] ? 'approved' : 'pending'}`}>{r[5] ? 'Active' : 'Off'}</span></td>
              <td className="action-cell">
                <button className="btn-xs btn-warning" onClick={() => toggleBanner.mutate(r[0])}>Toggle</button>
                <button className="btn-xs btn-danger" onClick={() => { if (confirm('Xoá banner?')) deleteBanner.mutate(r[0]) }}>Xoá</button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  )
}

// ─── FR-025: Analytics Tab ────────────────────────────────────────────────────

function AnalyticsTab() {
  const [days, setDays] = useState(30)
  const { data: overview } = useQuery({ queryKey: ['analytics-overview', days], queryFn: () => adminAPI.getAnalyticsOverview(days) })
  const { data: funnel } = useQuery({ queryKey: ['analytics-funnel'], queryFn: () => adminAPI.getOnboardingFunnel() })
  const { data: skills } = useQuery({ queryKey: ['analytics-skills'], queryFn: () => adminAPI.getSkillUsage() })
  const { data: topUsers } = useQuery({ queryKey: ['analytics-top-users'], queryFn: () => adminAPI.getTopUsers(10) })
  const { data: retention } = useQuery({ queryKey: ['analytics-retention'], queryFn: () => adminAPI.getRetentionCohorts() })

  const o = overview
  return (
    <div className="admin-tab-panel">
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Analytics Dashboard</h3>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ marginLeft: 'auto' }}>
          {[7, 14, 30, 90].map((d) => <option key={d} value={d}>{d} ngày</option>)}
        </select>
      </div>

      {o && (
        <div className="monitor-grid">
          <div className="monitor-card"><div className="monitor-label">Người dùng mới</div><div className="monitor-value">{o.newUsers}</div></div>
          <div className="monitor-card"><div className="monitor-label">Người dùng active</div><div className="monitor-value">{o.dau}</div></div>
          <div className="monitor-card"><div className="monitor-label">Tổng phiên học</div><div className="monitor-value">{o.totalSessions}</div></div>
          <div className="monitor-card"><div className="monitor-label">TB phút/phiên</div><div className="monitor-value">{o.avgSessionMin}</div></div>
          <div className="monitor-card"><div className="monitor-label">Bài Writing</div><div className="monitor-value">{o.writingSubmissions}</div></div>
          <div className="monitor-card"><div className="monitor-label">Bài Speaking</div><div className="monitor-value">{o.speakingSubmissions}</div></div>
          <div className="monitor-card"><div className="monitor-label">Chi phí AI (USD)</div><div className="monitor-value">${o.aiCostUsd}</div></div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 24 }}>
        <div className="stats-section">
          <h4>Onboarding Funnel</h4>
          <table className="admin-table">
            <thead><tr><th>Bước</th><th>Số lượng</th><th>Tỷ lệ</th></tr></thead>
            <tbody>{((funnel as any[]) ?? []).map((step: any, i: number, arr: any[]) => {
              const pct = i === 0 ? 100 : Math.round((step.count / arr[0].count) * 100)
              return <tr key={i}><td>{step.step}</td><td>{step.count}</td><td>{pct}%</td></tr>
            })}</tbody>
          </table>
        </div>

        <div className="stats-section">
          <h4>Kỹ năng được luyện tập nhiều nhất</h4>
          <table className="admin-table">
            <thead><tr><th>Kỹ năng</th><th>Phiên</th><th>XP TB</th></tr></thead>
            <tbody>{((skills as any[]) ?? []).map((r: any, i: number) => (
              <tr key={i}><td>{r[0]}</td><td>{r[1]}</td><td>{Number(r[2] ?? 0).toFixed(0)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </div>

      {/* Cohort Retention Matrix — G-3 */}
      <div className="stats-section" style={{ marginTop: 24 }}>
        <h4>Cohort Retention (90 ngày gần nhất)</h4>
        <p style={{ fontSize: '0.8125rem', color: '#6c757d', marginBottom: 12 }}>
          Mỗi hàng = nhóm user đăng ký trong tuần đó. D7 = % quay lại trong 7 ngày, D30 = trong 30 ngày.
        </p>
        {!retention || (retention as any[]).length === 0 ? (
          <p style={{ color: '#a5adc6', fontStyle: 'italic', fontSize: '0.875rem' }}>Chưa đủ dữ liệu.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Tuần đăng ký</th>
                  <th>Cohort size</th>
                  <th>D7 retention</th>
                  <th>D30 retention</th>
                  <th>D7 bar</th>
                </tr>
              </thead>
              <tbody>
                {(retention as any[]).map((r: any, i: number) => {
                  const cohortWeek = r[0]
                  const cohortSize = Number(r[1] ?? 0)
                  const d7 = Number(r[2] ?? 0)
                  const d30 = Number(r[3] ?? 0)
                  const d7Pct = cohortSize > 0 ? Math.round((d7 / cohortSize) * 100) : 0
                  const d30Pct = cohortSize > 0 ? Math.round((d30 / cohortSize) * 100) : 0
                  const barColor = d7Pct >= 40 ? '#006571' : d7Pct >= 20 ? '#004be2' : '#b41340'
                  return (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>
                        {cohortWeek ? new Date(cohortWeek).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }) : '—'}
                      </td>
                      <td>{cohortSize}</td>
                      <td>
                        <span style={{ fontWeight: 700, color: barColor }}>{d7Pct}%</span>
                        <span style={{ color: '#a5adc6', fontSize: '0.75rem', marginLeft: 4 }}>({d7})</span>
                      </td>
                      <td>
                        <span style={{ fontWeight: 700 }}>{d30Pct}%</span>
                        <span style={{ color: '#a5adc6', fontSize: '0.75rem', marginLeft: 4 }}>({d30})</span>
                      </td>
                      <td style={{ minWidth: 120 }}>
                        <div style={{ height: 8, background: '#eef0ff', borderRadius: 9999, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${d7Pct}%`, background: barColor, borderRadius: 9999, transition: 'width 0.4s' }} />
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="stats-section" style={{ marginTop: 24 }}>
        <h4>Top 10 người dùng (theo XP)</h4>
        <table className="admin-table">
          <thead><tr><th>Email</th><th>Gói</th><th>XP</th><th>Streak</th><th>Phiên học</th></tr></thead>
          <tbody>{((topUsers as any[]) ?? []).map((r: any, i: number) => (
            <tr key={i}>
              <td>{r[0]}</td>
              <td><span className={`badge-tier ${r[2]}`}>{r[2]}</span></td>
              <td><strong>{Number(r[3]).toLocaleString()}</strong></td>
              <td>{r[4]} ngày</td>
              <td>{r[5]}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Shared ───────────────────────────────────────────────────────────────────

function Pagination({ page, setPage }: { page: number; setPage: (fn: (p: number) => number) => void }) {
  return (
    <div className="pagination">
      <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>← Trang trước</button>
      <span>Trang {page}</span>
      <button onClick={() => setPage((p) => p + 1)}>Trang sau →</button>
    </div>
  )
}
